import React from "react";
import SEO from "@/lib/seo-static.json";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { CATEGORIES, MODULES, FLAGSHIPS, modulesIn } from "@/lib/modules";
import { Container, Section, SectionHeader, Reveal, Kicker, usePageTitle } from "@/components/ui-kit";
import { ModuleCard } from "@/components/sections/ModuleExplorer";
import NewModules from "@/components/sections/NewModules";
import { AIMatchSection } from "@/components/sections/Showcases";
import { CTABand } from "@/components/sections/Social";

export default function Product() {
  usePageTitle(SEO["/product"].title, SEO["/product"].description, SEO["/product"].image);
  return (
    <div data-testid="product-page">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-dark mask-fade-b opacity-60" aria-hidden />
        <div className="orb h-[460px] w-[460px] -top-40 left-1/2 -translate-x-1/2 bg-mk-lime/10" aria-hidden />
        <Container className="relative pt-14 pb-10 lg:pt-20 text-center">
          <Kicker>{MODULES.length} modules · {CATEGORIES.length} lanes · one engine</Kicker>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight text-mk-text leading-[1] text-balance">Every inch of the platform.</h1>
          <p className="mt-6 text-base md:text-lg text-mk-dim leading-relaxed max-w-2xl mx-auto">Jobs, quotes, invoices, operatives, suppliers, compliance, assets, AI, webhooks — if it touches maintenance, Fixlr has a dedicated, production-grade module for it.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-2">
            {CATEGORIES.map((c) => <a key={c.slug} href={`#${c.slug}`} data-testid={`product-jump-${c.slug}`} className="rounded-full border border-mk-border bg-mk-surface px-4 py-2 text-sm text-mk-dim hover:text-mk-text hover:border-mk-lime/50 transition-colors">{c.name} <span className="font-mono text-[10px] text-mk-muted">{modulesIn(c.slug).length}</span></a>)}
          </div>
        </Container>
      </section>

      <Section className="pt-8">
        <Container>
          <SectionHeader eyebrow="Flagships" title={`${FLAGSHIPS.length} modules with a full deep-dive.`} desc="The ones customers switch for. Each has its own page: the problem, the screens, how it works, FAQs." />
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">{FLAGSHIPS.map((m) => <ModuleCard key={m.slug} m={m} testIdPrefix="product-flagship" />)}</div>
        </Container>
      </Section>

      <NewModules />

      {CATEGORIES.map((c, i) => (
        <Section key={c.slug} id={c.slug} className={`scroll-mt-24 ${i % 2 === 0 ? "bg-mk-surface/40 border-y border-mk-border" : ""}`} data-testid={`product-cat-${c.slug}`}>
          <Container>
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
              <SectionHeader eyebrow={c.kicker} title={c.headline} desc={c.desc} />
              <Link to={`/platform/${c.slug}`} data-testid={`product-cat-link-${c.slug}`} className="btn-ghost self-start shrink-0">{c.name} in depth <ArrowRight className="h-4 w-4" /></Link>
            </div>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {modulesIn(c.slug).map((m, j) => <Reveal key={m.slug} delay={j * 0.05}><ModuleCard m={m} testIdPrefix="product-module" /></Reveal>)}
            </div>
          </Container>
        </Section>
      ))}

      <AIMatchSection />
      <CTABand />
    </div>
  );
}
